/* global google */
import React, { useState, useRef, useEffect } from "react"
import PlacesAutocomplete, {
  getLatLng,
  geocodeByAddress,
} from "react-places-autocomplete"
import { createUseStyles } from "react-jss"
import classNames from "classnames"

import PlaceField from "./place-input"
import colors from "../../styles/colors"
import { includesAll } from "../../util/array"

const unit = 8

const geocoder = new google.maps.Geocoder()

const useStyles = createUseStyles({
  root: {
    width: "100%",
  },
  autocompleteList: {
    zIndex: 9999,
    position: "absolute",
    top: 56,
    backgroundColor: "white",
    width: "100%",
    paddingLeft: 0,
    marginTop: 0,
    marginBottom: 0,
    border: "1px solid rgb(85, 85, 85)",
    "& li": {
      padding: unit,
      color: "rgb(85, 85, 85)",
      cursor: "pointer",
      fontSize: 14,
      "&:hover": {
        backgroundColor: colors.gray2,
      },
    },
  },
  autocompleteListEmpty: {
    display: "none",
    marginTop: 0,
    marginBottom: 0,
  },
  activeOption: {
    backgroundColor: colors.gray13,
    outline: `1px solid ${colors.blue1}`,
  },
  error: {
    extend: "input",
    border: `1px solid ${colors.red1}`,
    "&:focus": {
      outline: "none",
    },
  },
  autocompleteItem: {
    fontSize: 14,
    padding: unit,
  },
  placeAutocompleteWrapper: {
    position: "relative",
    width: "100%",
    marginBottom: 0,
    "& label": {
      color: "#031D3C99",
    },
  },
  placesInputWrapper: {
    display: "block",
  },
  googleLogo: {
    display: "block",
    height: "auto",
    width: 100,
    marginLeft: "auto",
    boxSizing: "border-box",
  },
})

const getLocationData = async (coordinates) => {
  return new Promise((resolve, reject) => {
    geocoder.geocode({ location: coordinates }, function (results, status) {
      if (status === "OK") {
        if (results[0]) {
          resolve(results[0])
        }
      }

      reject(status)
    })
  })
}
const searchOptions = {
  componentRestrictions: { country: "us" },
}
const errorMessage = "A valid address is required."

const PlaceInputAutocomplete = ({
  placeholder = "",
  addressFormatter = null,
  backgroundColor = "",
  icon,
  label,
  name,
  value,
  onChange,
  onError,
  error,
  className = "",
}) => {
  const [address, setAddress] = useState("")
  const [checkOnBlur, setCheckOnBlur] = useState(true)
  const inputRef = useRef(null)
  const suggestionsRef = useRef(null)
  const classes = useStyles()

  const handleError = () => {
    onChange(null)
    onError(errorMessage)
  }

  useEffect(() => {
    if (value && value.formatted_address) {
      setAddress(value.formatted_address)
    } else {
      setAddress("")
    }
  }, [value])

  const updateAddressValue = (address) => {
    if (!checkOnBlur) {
      setCheckOnBlur(true)
    }
    setAddress(address)
  }

  const getAddressProperty = (addressComponents, addressType, property) => {
    const matchingComponents = addressComponents.filter((cmp) =>
      cmp.types.includes(addressType),
    )

    // NOTE: What if we have more than one matching property?
    if (matchingComponents.length > 0) {
      return matchingComponents[0][property] || null
    }

    return null
  }

  const getCityName = (addressComponents) => {
    const locality = addressComponents.find((addressComponent) => {
      return addressComponent.types.includes("locality")
    })

    if (locality) {
      // NOTE: Do we want short_name or long_name here? In testing short_name
      // has been working fine.
      return locality.short_name
    }

    // Administrative_area_level_1 amd political types indicate a state in the US.
    // NYC seems to be a special case where google thinks it's not a city but it
    // is state. So we just hard-code New York as a special case.
    const adminLevel1AndPolitical = addressComponents.find(
      (addressComponent) => {
        return includesAll(addressComponent.types, [
          "administrative_area_level_1",
          "political",
        ])
      },
    )
    if (
      adminLevel1AndPolitical &&
      adminLevel1AndPolitical.short_name === "NY"
    ) {
      return adminLevel1AndPolitical.long_name
    }

    return null
  }

  function compareAddressToSuggestions(address, suggestions) {
    const addressMatchedSuggestion = suggestions.find((suggestion) => {
      return suggestion.description.toLowerCase() === address.toLowerCase()
    })

    return addressMatchedSuggestion
  }

  const getAddressData = async (address) => {
    if (!compareAddressToSuggestions(address, suggestionsRef.current)) {
      return
    }

    try {
      const response = await geocodeByAddress(address)
      const coordinates = await getLatLng(response[0])
      const locationData = await getLocationData(coordinates)

      const cityName = getCityName(locationData.address_components)

      const stateShortName = getAddressProperty(
        locationData.address_components,
        "administrative_area_level_1",
        "short_name",
      )

      const zipCode = getAddressProperty(
        locationData.address_components,
        "postal_code",
        "long_name",
      )

      const routeData = {
        formatted_address: response[0].formatted_address,
        city: cityName,
        state_code: stateShortName,
        lat: coordinates.lat,
        lng: coordinates.lng,
        zip: zipCode,
      }

      if (addressFormatter) {
        routeData.formatted_address = addressFormatter(routeData)
      }

      setAddress(address)
      onChange(routeData)
      setCheckOnBlur(false)
      onError(null)
    } catch (error) {
      onError(errorMessage)
      onChange(null)
      setAddress("")
    }
  }

  const handleBlur = () => {
    inputRef.current.blur()
    if (!checkOnBlur) {
      return false
    }

    if (compareAddressToSuggestions(address, suggestionsRef.current)) {
      getAddressData(address)
    } else {
      onError(errorMessage)
      onChange(null)
      setAddress("")
    }
  }

  return (
    <div className={classNames(classes.placesInputWrapper, className)}>
      <PlacesAutocomplete
        value={address}
        onChange={(address) => {
          updateAddressValue(address)
        }}
        onSelect={getAddressData}
        onError={handleError}
        searchOptions={searchOptions}
        highlightFirstSuggestion
      >
        {({ getInputProps, suggestions, getSuggestionItemProps }) => {
          // Store genereated suggestions, so that the entered text can be matched against those on the enter key and on a blur event
          // Pressing enter key also triggers onSelect event
          suggestionsRef.current = suggestions

          return (
            <div className={classes.placeAutocompleteWrapper}>
              <PlaceField
                icon={icon}
                label={label}
                name={name}
                id={name}
                title={name}
                ref={inputRef}
                error={error}
                backgroundColor={backgroundColor}
                {...getInputProps({
                  placeholder: placeholder,
                  onBlur: () => {
                    handleBlur()
                  },
                })}
              />
              <ul
                className={
                  suggestions.length
                    ? classes.autocompleteList
                    : classes.autocompleteListEmpty
                }
              >
                {suggestions.map((suggestion) => {
                  return (
                    <li
                      {...getSuggestionItemProps(suggestion, {
                        className: suggestion.active
                          ? classes.activeOption
                          : null,
                      })}
                      key={suggestion.placeId}
                    >
                      <span>{suggestion.description}</span>
                    </li>
                  )
                })}
              </ul>
            </div>
          )
        }}
      </PlacesAutocomplete>
    </div>
  )
}

export default PlaceInputAutocomplete
